import { useState, useEffect, Fragment } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon, ExclamationTriangleIcon, ArrowRightIcon } from '@heroicons/react/20/solid';

function DeDupeKanban() {
  document.title = 'Migrate Kanban Data';

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [duplicates, setDuplicates] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  const fetchData = async () => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    // Verify user role
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('role')
      .eq('id', user.id)
      .single();

    if (userError || (userData?.role !== 'admin' && userData?.role !== 'owner')) {
      console.error('Access denied:', userError?.message || 'Insufficient permissions');
      navigate('/access-denied');
      return;
    }

    setLoading(true);
    try {
      // Fetch legacy kanban jobs (no kanban_job_id, no due date)
      const { data: legacyJobs, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          id, po_number, po_line_number, part_number, description,
          quantity_on_order, shipped_quantity, produced_quantity, unit_price, status, created_at,
          customer_id, shop_id,
          customers(id, name, nickname),
          shops(id, name)
        `)
        .is('kanban_job_id', null)
        .is('expected_delivery_date', null);

      if (jobsError) throw jobsError;

      // Fetch all kanban_jobs
      const { data: kanbanJobs, error: kanbanError } = await supabase
        .from('kanban_jobs')
        .select(`
          id, po_number, po_line_number, part_number, description,
          quantity_on_order, shipped_quantity, produced_quantity, unit_price, status, created_at,
          customer_id,
          customers(id, name, nickname)
        `);

      if (kanbanError) throw kanbanError;

      // Get all legacy job IDs
      const legacyJobIds = legacyJobs.map(job => job.id);
      const kanbanJobIds = kanbanJobs.map(kj => kj.id);

      // Fetch deliveries linked to legacy jobs
      const { data: legacyDeliveries } = await supabase
        .from('deliveries')
        .select('delivery_id, job_id, kanban_job_id, quantity')
        .in('job_id', legacyJobIds.length > 0 ? legacyJobIds : [-1]);

      // Fetch deliveries linked to kanban_jobs
      const { data: kanbanDeliveries } = await supabase
        .from('deliveries')
        .select('delivery_id, job_id, kanban_job_id, quantity')
        .in('kanban_job_id', kanbanJobIds.length > 0 ? kanbanJobIds : [-1]);

      // Fetch production logs linked to legacy jobs
      const { data: prodLogs } = await supabase
        .from('production_logs')
        .select('log_id, job_id, quantity_produced')
        .in('job_id', legacyJobIds.length > 0 ? legacyJobIds : [-1]);

      // Build lookup maps
      const kanbanByPoLine = kanbanJobs.reduce((acc, kj) => {
        const key = `${kj.po_number}-${kj.po_line_number}`;
        acc[key] = kj;
        return acc;
      }, {});

      const legacyDeliveriesByJob = (legacyDeliveries || []).reduce((acc, del) => {
        if (!acc[del.job_id]) acc[del.job_id] = [];
        acc[del.job_id].push(del);
        return acc;
      }, {});

      const kanbanDeliveriesByKanban = (kanbanDeliveries || []).reduce((acc, del) => {
        if (!acc[del.kanban_job_id]) acc[del.kanban_job_id] = [];
        acc[del.kanban_job_id].push(del);
        return acc;
      }, {});

      const prodLogsByJob = (prodLogs || []).reduce((acc, log) => {
        if (!acc[log.job_id]) acc[log.job_id] = [];
        acc[log.job_id].push(log);
        return acc;
      }, {});

      // Separate duplicates (exist in both tables) from orphans (only in jobs)
      const duplicatesList = [];
      const orphansList = [];

      legacyJobs.forEach(job => {
        const key = `${job.po_number}-${job.po_line_number}`;
        const kanban = kanbanByPoLine[key];
        const jobDeliveries = legacyDeliveriesByJob[job.id] || [];
        const jobProdLogs = prodLogsByJob[job.id] || [];

        if (kanban) {
          // Duplicate - exists in both tables
          const kanbanDeliveriesArr = kanbanDeliveriesByKanban[kanban.id] || [];
          duplicatesList.push({
            job,
            kanban,
            qtyMatch: job.quantity_on_order === kanban.quantity_on_order,
            priceMatch: parseFloat(job.unit_price) === parseFloat(kanban.unit_price),
            shippedMatch: job.shipped_quantity === kanban.shipped_quantity,
            legacyDeliveryCount: jobDeliveries.length,
            legacyDeliveryQty: jobDeliveries.reduce((sum, d) => sum + d.quantity, 0),
            kanbanDeliveryCount: kanbanDeliveriesArr.length,
            kanbanDeliveryQty: kanbanDeliveriesArr.reduce((sum, d) => sum + d.quantity, 0),
            productionLogCount: jobProdLogs.length,
            productionQty: jobProdLogs.reduce((sum, l) => sum + l.quantity_produced, 0),
            needsMigration: jobDeliveries.length > 0,
          });
        } else {
          // Orphan - only in jobs table
          orphansList.push({
            job,
            deliveryCount: jobDeliveries.length,
            deliveryQty: jobDeliveries.reduce((sum, d) => sum + d.quantity, 0),
            productionLogCount: jobProdLogs.length,
            productionQty: jobProdLogs.reduce((sum, l) => sum + l.quantity_produced, 0),
          });
        }
      });

      // Sort: needs migration first, then by PO
      duplicatesList.sort((a, b) => {
        if (a.needsMigration && !b.needsMigration) return -1;
        if (!a.needsMigration && b.needsMigration) return 1;
        return a.job.po_number.localeCompare(b.job.po_number);
      });

      setDuplicates(duplicatesList);
      setOrphans(orphansList);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, authLoading, navigate]);

  // Migrate deliveries from legacy job to kanban_jobs
  const handleMigrateDeliveries = (dup) => {
    setConfirmModal({
      open: true,
      title: 'Migrate Deliveries',
      message: `Migrate ${dup.legacyDeliveryCount} delivery record(s) from legacy jobs entry to kanban_jobs for ${dup.job.po_number} line ${dup.job.po_line_number}?\n\nThis will:\n• Set kanban_job_id = ${dup.kanban.id}\n• Clear job_id reference`,
      confirmText: 'Migrate',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessing(dup.job.id);
        try {
          // Update deliveries: set kanban_job_id and clear job_id
          const { error } = await supabase
            .from('deliveries')
            .update({
              kanban_job_id: dup.kanban.id,
              job_id: null
            })
            .eq('job_id', dup.job.id);

          if (error) throw error;

          // Refresh data
          await fetchData();
          toast.success(`Migrated ${dup.legacyDeliveryCount} deliveries to kanban_jobs`);
        } catch (err) {
          console.error('Error migrating:', err);
          toast.error(`Failed to migrate: ${err.message}`);
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  // Delete legacy job entry (only when no related data)
  const handleDeleteLegacy = (dup) => {
    const hasData = dup.legacyDeliveryCount > 0 || dup.productionLogCount > 0;

    if (hasData) {
      toast.error('Cannot delete: migrate deliveries first');
      return;
    }

    setConfirmModal({
      open: true,
      title: 'Delete Legacy Entry',
      message: `Delete legacy jobs entry for ${dup.job.po_number} line ${dup.job.po_line_number}?\n\nThe kanban_jobs entry (id=${dup.kanban.id}) will remain as the source of truth.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessing(dup.job.id);
        try {
          const { error } = await supabase
            .from('jobs')
            .delete()
            .eq('id', dup.job.id);

          if (error) throw error;

          setDuplicates(prev => prev.filter(d => d.job.id !== dup.job.id));
          toast.success(`Deleted legacy entry for ${dup.job.po_number} line ${dup.job.po_line_number}`);
        } catch (err) {
          console.error('Error deleting:', err);
          toast.error(`Failed to delete: ${err.message}`);
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  // Migrate and delete in one action
  const handleMigrateAndDelete = (dup) => {
    setConfirmModal({
      open: true,
      title: 'Migrate & Delete',
      message: `For ${dup.job.po_number} line ${dup.job.po_line_number}:\n\n1. Migrate ${dup.legacyDeliveryCount} delivery record(s) to kanban_jobs\n2. Delete the legacy jobs entry\n\nThis cannot be undone.`,
      confirmText: 'Migrate & Delete',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessing(dup.job.id);
        try {
          // Step 1: Migrate deliveries
          if (dup.legacyDeliveryCount > 0) {
            const { error: migrateError } = await supabase
              .from('deliveries')
              .update({
                kanban_job_id: dup.kanban.id,
                job_id: null
              })
              .eq('job_id', dup.job.id);

            if (migrateError) throw migrateError;
          }

          // Step 2: Delete legacy job
          const { error: deleteError } = await supabase
            .from('jobs')
            .delete()
            .eq('id', dup.job.id);

          if (deleteError) throw deleteError;

          setDuplicates(prev => prev.filter(d => d.job.id !== dup.job.id));
          toast.success(`Migrated and deleted ${dup.job.po_number} line ${dup.job.po_line_number}`);
        } catch (err) {
          console.error('Error:', err);
          toast.error(`Failed: ${err.message}`);
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  // Create kanban_jobs entry for orphan and migrate
  const handleCreateAndMigrate = (orphan) => {
    setConfirmModal({
      open: true,
      title: 'Create & Migrate',
      message: `Create kanban_jobs entry for ${orphan.job.po_number} line ${orphan.job.po_line_number} and migrate all data?\n\n• ${orphan.deliveryCount} delivery record(s)\n• ${orphan.productionLogCount} production log(s)\n\nThe legacy jobs entry will be deleted after migration.`,
      confirmText: 'Create & Migrate',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessing(orphan.job.id);
        try {
          // Step 1: Create kanban_jobs entry
          const { data: newKanban, error: createError } = await supabase
            .from('kanban_jobs')
            .insert({
              po_number: orphan.job.po_number,
              po_line_number: orphan.job.po_line_number,
              part_number: orphan.job.part_number,
              description: orphan.job.description,
              quantity_on_order: orphan.job.quantity_on_order,
              unit_price: orphan.job.unit_price,
              shipped_quantity: orphan.job.shipped_quantity,
              produced_quantity: orphan.job.produced_quantity,
              status: orphan.job.status,
              customer_id: orphan.job.customer_id,
              shop_id: orphan.job.shop_id || 1,
            })
            .select()
            .single();

          if (createError) throw createError;

          // Step 2: Migrate deliveries
          if (orphan.deliveryCount > 0) {
            const { error: migrateError } = await supabase
              .from('deliveries')
              .update({
                kanban_job_id: newKanban.id,
                job_id: null
              })
              .eq('job_id', orphan.job.id);

            if (migrateError) throw migrateError;
          }

          // Step 3: Delete legacy job (production logs will be orphaned but there are none based on data)
          const { error: deleteError } = await supabase
            .from('jobs')
            .delete()
            .eq('id', orphan.job.id);

          if (deleteError) throw deleteError;

          setOrphans(prev => prev.filter(o => o.job.id !== orphan.job.id));
          toast.success(`Created kanban_jobs entry and migrated ${orphan.job.po_number} line ${orphan.job.po_line_number}`);
        } catch (err) {
          console.error('Error:', err);
          toast.error(`Failed: ${err.message}`);
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  // Migrate all duplicates
  const handleMigrateAll = () => {
    const toMigrate = duplicates.filter(d => d.legacyDeliveryCount > 0 || d.productionLogCount === 0);

    if (toMigrate.length === 0) {
      toast.info('No entries to migrate');
      return;
    }

    setConfirmModal({
      open: true,
      title: 'Migrate All',
      message: `Migrate and delete ${toMigrate.length} legacy entries?\n\nThis will:\n• Re-link all deliveries to kanban_jobs\n• Delete all legacy jobs entries\n\nThis cannot be undone.`,
      confirmText: `Migrate ${toMigrate.length}`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessing('all');
        try {
          let migrated = 0;
          for (const dup of toMigrate) {
            // Migrate deliveries
            if (dup.legacyDeliveryCount > 0) {
              const { error: migrateError } = await supabase
                .from('deliveries')
                .update({
                  kanban_job_id: dup.kanban.id,
                  job_id: null
                })
                .eq('job_id', dup.job.id);

              if (migrateError) {
                console.error(`Failed to migrate ${dup.job.po_number}:`, migrateError);
                continue;
              }
            }

            // Delete legacy job
            const { error: deleteError } = await supabase
              .from('jobs')
              .delete()
              .eq('id', dup.job.id);

            if (deleteError) {
              console.error(`Failed to delete ${dup.job.po_number}:`, deleteError);
              continue;
            }

            migrated++;
          }

          await fetchData();
          toast.success(`Migrated and deleted ${migrated} legacy entries`);
        } catch (err) {
          console.error('Error:', err);
          toast.error(`Failed: ${err.message}`);
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  const needsMigrationCount = duplicates.filter(d => d.legacyDeliveryCount > 0).length;
  const readyToDeleteCount = duplicates.filter(d => d.legacyDeliveryCount === 0 && d.productionLogCount === 0).length;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-800">Migrate Kanban Data</h1>
        <p className="text-sm text-slate-500 mt-1">
          Migrate legacy kanban entries from jobs table to kanban_jobs and re-link deliveries
        </p>
      </div>

      <div className="p-6">
        {/* Summary */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-6">
              <div>
                <p className="text-2xl font-semibold text-slate-800">{duplicates.length}</p>
                <p className="text-sm text-slate-500">Duplicates</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-600">{needsMigrationCount}</p>
                <p className="text-sm text-slate-500">Need Migration</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-green-600">{readyToDeleteCount}</p>
                <p className="text-sm text-slate-500">Ready to Delete</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-blue-600">{orphans.length}</p>
                <p className="text-sm text-slate-500">Orphans</p>
              </div>
            </div>
            {duplicates.length > 0 && (
              <button
                onClick={handleMigrateAll}
                disabled={processing === 'all'}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {processing === 'all' ? 'Processing...' : `Migrate All ${duplicates.length}`}
              </button>
            )}
          </div>
        </div>

        {/* Orphans Section */}
        {orphans.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-slate-800 mb-3">
              Orphans (Only in jobs table)
            </h2>
            <div className="space-y-3">
              {orphans.map((orphan) => {
                const isProcessing = processing === orphan.job.id;
                return (
                  <div
                    key={orphan.job.id}
                    className="bg-white rounded-lg border border-blue-300 overflow-hidden"
                  >
                    <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <ArrowRightIcon className="w-5 h-5 text-blue-500" />
                        <div>
                          <p className="font-medium text-slate-800">
                            {orphan.job.po_number} - Line {orphan.job.po_line_number}
                          </p>
                          <p className="text-sm text-slate-500">
                            {orphan.job.part_number} • {orphan.job.customers?.nickname || orphan.job.customers?.name}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            {orphan.deliveryCount} deliveries • {orphan.productionLogCount} prod logs •
                            Qty: {orphan.job.quantity_on_order} • Shipped: {orphan.job.shipped_quantity}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCreateAndMigrate(orphan)}
                        disabled={isProcessing}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Create & Migrate'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Duplicates Section */}
        {duplicates.length === 0 && orphans.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <CheckIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-800">All data migrated!</p>
            <p className="text-sm text-slate-500 mt-1">No legacy kanban entries remain in the jobs table.</p>
          </div>
        ) : duplicates.length > 0 && (
          <>
            <h2 className="text-lg font-medium text-slate-800 mb-3">
              Duplicates (Exist in both tables)
            </h2>
            <div className="space-y-4">
              {duplicates.map((dup) => {
                const isProcessing = processing === dup.job.id;
                const canDelete = dup.legacyDeliveryCount === 0 && dup.productionLogCount === 0;

                return (
                  <div
                    key={dup.job.id}
                    className={`bg-white rounded-lg border ${dup.needsMigration ? 'border-amber-300' : 'border-green-300'} overflow-hidden`}
                  >
                    {/* Header */}
                    <div className={`px-4 py-3 ${dup.needsMigration ? 'bg-amber-50' : 'bg-green-50'} border-b border-slate-200 flex items-center justify-between`}>
                      <div className="flex items-center gap-3">
                        {dup.needsMigration ? (
                          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
                        ) : (
                          <CheckIcon className="w-5 h-5 text-green-500" />
                        )}
                        <div>
                          <p className="font-medium text-slate-800">
                            {dup.job.po_number} - Line {dup.job.po_line_number}
                          </p>
                          <p className="text-sm text-slate-500">
                            {dup.job.part_number} • {dup.job.customers?.nickname || dup.job.customers?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {dup.needsMigration ? (
                          <>
                            <button
                              onClick={() => handleMigrateDeliveries(dup)}
                              disabled={isProcessing}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              Migrate Only
                            </button>
                            <button
                              onClick={() => handleMigrateAndDelete(dup)}
                              disabled={isProcessing}
                              className="px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded hover:bg-amber-700 disabled:opacity-50"
                            >
                              {isProcessing ? 'Processing...' : 'Migrate & Delete'}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleDeleteLegacy(dup)}
                            disabled={isProcessing || !canDelete}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {isProcessing ? 'Processing...' : 'Delete Legacy'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Delivery Comparison */}
                    <div className="p-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="text-left py-1 font-medium w-40">Source</th>
                            <th className="text-right py-1 font-medium">Qty Ordered</th>
                            <th className="text-right py-1 font-medium">Shipped</th>
                            <th className="text-right py-1 font-medium">Unit Price</th>
                            <th className="text-right py-1 font-medium">Deliveries</th>
                            <th className="text-right py-1 font-medium">Del. Qty</th>
                            <th className="text-right py-1 font-medium">Prod Logs</th>
                            <th className="text-left py-1 font-medium pl-4">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-slate-100">
                            <td className="py-2">
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                                jobs (legacy)
                              </span>
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.qtyMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.job.quantity_on_order}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.shippedMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.job.shipped_quantity || 0}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.priceMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {formatCurrency(dup.job.unit_price)}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${dup.legacyDeliveryCount > 0 ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.legacyDeliveryCount}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${dup.legacyDeliveryQty > 0 ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.legacyDeliveryQty}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${dup.productionLogCount > 0 ? 'text-red-600 font-medium' : ''}`}>
                              {dup.productionLogCount}
                            </td>
                            <td className="py-2 pl-4 text-slate-500 text-xs">
                              {new Date(dup.job.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                          <tr className="border-t border-slate-100">
                            <td className="py-2">
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                kanban_jobs
                              </span>
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.qtyMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.kanban.quantity_on_order}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.shippedMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {dup.kanban.shipped_quantity || 0}
                            </td>
                            <td className={`py-2 text-right tabular-nums ${!dup.priceMatch ? 'text-amber-600 font-medium' : ''}`}>
                              {formatCurrency(dup.kanban.unit_price)}
                            </td>
                            <td className="py-2 text-right tabular-nums text-green-600">
                              {dup.kanbanDeliveryCount}
                            </td>
                            <td className="py-2 text-right tabular-nums text-green-600">
                              {dup.kanbanDeliveryQty}
                            </td>
                            <td className="py-2 text-right tabular-nums text-slate-400">
                              —
                            </td>
                            <td className="py-2 pl-4 text-slate-500 text-xs">
                              {new Date(dup.kanban.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <Transition appear show={confirmModal.open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-slate-900">
                    {confirmModal.title}
                  </Dialog.Title>
                  <div className="mt-3">
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">
                      {confirmModal.message}
                    </p>
                  </div>

                  <div className="mt-5 flex justify-end gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                      onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      onClick={confirmModal.onConfirm}
                    >
                      {confirmModal.confirmText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default DeDupeKanban;
